import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GlassCard from "../../components/GlassCard";
import { getTodayPlan } from "../api/planApi";
import { todaysSessionText } from "../data/platformText";
import ErrorPage from "../pages/ErrorPage";
import LoadingPage from "../pages/LoadingPage";

function isExerciseItemCompleted(item) {
  return item?.status === "completed" || Boolean(item?.completed);
}

function isRealExerciseItem(item) {
  return item?.exercise_id !== null && item?.exercise_id !== undefined;
}

function isSessionCompletedFromExercises(session) {
  const realExercises = (session?.exercises || []).filter(isRealExerciseItem);

  return (
    realExercises.length > 0 &&
    realExercises.every(isExerciseItemCompleted)
  );
}

function isSessionCompleted(session) {
  return (
    session?.status === "completed" ||
    session?.completed === true ||
    isSessionCompletedFromExercises(session)
  );
}

const TodayPlanCard = () => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const t = todaysSessionText[lang];

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  useEffect(() => {
    async function loadPlan() {
      try {
        setLoading(true);
        setError("");
        setWarning("");

        const data = await getTodayPlan();

        if (!data) {
          setPlan(null);
          setWarning("No active plan assigned to the user.");
          return;
        }

        setPlan(data);
      } catch (err) {
        setError(err.message || t.failPlan);
      } finally {
        setLoading(false);
      }
    }

    loadPlan();
  }, []);

  const sessions = plan?.sessions ?? [];

  const normalizedSessions = sessions.map((session) => ({
    ...session,
    completed: isSessionCompleted(session),
  }));

  const activeSessions = normalizedSessions.filter((session) => !session.completed);
  const completedSessions = normalizedSessions.filter((session) => session.completed);

  const handleGoToTodaysSession = (session) => {
    navigate(`/${lang}/platform/todays-sessions/${session.id}`);
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage err={error} />;
  }


  return (
    <GlassCard
      style={{
        padding: "20px",
        borderRadius: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "18px",
        minHeight: "280px",
        marginLeft: "50px",
        marginTop: "25px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <span
          style={{
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            opacity: 0.7,
          }}
        >
          {t.s}
        </span>

        <h3
          style={{
            margin: 0,
            fontSize: "1.3rem",
            fontWeight: 700,
            color: "#0b1720",
          }}
        >
          {activeSessions.length > 0 ? t.todayPlan1 : t.todayPlan2}
        </h3>

        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#4c6a7a",
          }}
        >
          {activeSessions.length > 0 ? t.activeSess1 : t.activeSess2}
        </p>
      </div>

      {sessions.length > 0 && (
        <GlassCard
          className="exercise-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px",
            height: "40px",
            background: "#eef5ff89",
            marginTop: "25px",
          }}
        >
          <div style={{ width: "250px", fontSize: "0.95rem", color: "#0b1720" }}>
            {t.hName}
          </div>
          <div style={{ fontSize: "0.95rem", color: "#0b1720" }}>
            {t.hDifficulty}
          </div>
          <div style={{ fontSize: "0.95rem", color: "#0b1720" }}>
            {t.hNofSets}
          </div>
          <div style={{ fontSize: "0.95rem", color: "#0b1720" }}>
            {t.hTime}
          </div>
        </GlassCard>
      )}

      {activeSessions.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {activeSessions.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() => handleGoToTodaysSession(session)}
              style={{
                border: "none",
                background: "rgba(255,255,255,0.28)",
                borderRadius: "16px",
                padding: "14px 16px",
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                transition: "all 0.2s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  width: "250px",
                }}
              >
                <span
                  style={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#0b1720",
                  }}
                >
                  {session.name}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: "0.92rem",
                    color: "#0b1720",
                  }}
                >
                  {session.hardness_score ?? "-"}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: "0.92rem",
                    color: "#0b1720",
                  }}
                >
                  -
                </span>
              </div>

              <span
                style={{
                  fontSize: "0.92rem",
                  color: "#0b1720",
                  whiteSpace: "nowrap",
                }}
              >
                {session.estimated_time ?? "-"}
              </span>
            </button>
          ))}
        </div>
      )}

      {completedSessions.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginTop: activeSessions.length > 0 ? "4px" : "0px",
          }}
        >
          <div
            style={{
              fontSize: "1.1rem",
              fontWeight: 700,
              color: "#0b1720",
            }}
          >
            Completed:
          </div>

          {completedSessions.map((session) => (
            <div
              key={session.id}
              style={{
                borderRadius: "16px",
                padding: "12px 16px",
                background: "rgba(255,255,255,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                opacity: 0.75,
              }}
            >
              <span
                style={{
                  fontSize: "0.98rem",
                  textDecoration: "line-through",
                  color: "#0b1720",
                  width: "250px",
                }}
              >
                {session.name}
              </span>

              <span
                style={{
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                  color: "#0b1720",
                }}
              >
                {session.hardness_score ?? "-"}
              </span>

              <span
                style={{
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                  color: "#0b1720",
                }}
              >
                -
              </span>

              <span
                style={{
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                  color: "#0b1720",
                }}
              >
                {session.estimated_time ?? "-"}
              </span>
            </div>
          ))}
        </div>
      )}

      {sessions.length === 0 && (
        <div
          style={{
            fontSize: "0.98rem",
            color: "#4c6a7a",
            marginTop: "8px",
          }}
        >
          {t.noScheduled}
        </div>
      )}

      {warning && (
        <div
          style={{
            fontSize: "0.98rem",
            color: "#4c6a7a",
            marginTop: "8px",
          }}
        >
          {warning}
        </div>
      )}
    </GlassCard>
  );
};

export default TodayPlanCard;